import { reactive, watch } from "vue";
import { DEFAULT_MATCH_RULE_SETTINGS, type MatchRuleSettings } from "../../shared/matchSettings.js";

export type GameSettingsPreset = "classic-13x20";

/**
 * How a player is drawn on the pitch.
 *
 * "shapes" is the football reading: discs, rounded squares and rhombuses with
 * a role letter. "chess" swaps in the chess symbol the role actually moves
 * like — keeper/king, defender/rook, midfielder/queen, striker/bishop — which
 * is not decoration but the movement rule itself, drawn on the piece.
 */
export type PieceStyle = "shapes" | "chess";

/**
 * Which board design to draw. "auto" follows the piece style — chess symbols
 * get the wooden board, football shapes get grass — which is what somebody
 * flipping one switch expects. Naming a theme id pins it regardless, which is
 * the hook a picker (or a shop item) plugs into; see shared/boardThemes.ts.
 */
export type BoardThemeSetting = "auto" | string;

type PresetConfig = {
  fieldWidth: number;
  fieldHeight: number;
  goalWidth: number;
  movementCapMax: number;
  passDistanceMax: number;
  saveZoneLength: number;
};

// The one preset restores the classic board; anything else is reached through
// the individual fields. (A "compact 8x12" preset existed and was retired —
// 13x20 is the game.) The zone table is the one number it collapsed into
// (see shared/matchSettings.ts).
const presets: Record<GameSettingsPreset, PresetConfig> = {
  "classic-13x20": {
    fieldWidth: 13,
    fieldHeight: 20,
    goalWidth: 7,
    movementCapMax: 5,
    passDistanceMax: 10,
    saveZoneLength: DEFAULT_MATCH_RULE_SETTINGS.saveZoneLength,
  },
};

/**
 * The rules half of these settings IS shared/matchSettings.ts -- one source of
 * defaults, so a tournament host's dictated rules and a local custom game can
 * never quietly disagree about what "default" means. The store adds only what
 * a host has no business dictating: device preferences.
 */
const defaultSettings = {
  ...DEFAULT_MATCH_RULE_SETTINGS,
  movementHints: false,
  soundEnabled: false,
  pieceStyle: "shapes" as PieceStyle,
  boardTheme: "auto" as BoardThemeSetting,
};

export type LocalGameSettings = typeof defaultSettings;

/**
 * A settings store: the reactive object plus the helpers that edit it.
 *
 * A factory rather than module-level functions, because two owners need one:
 * the app-wide singleton below (a device's local-game preferences), and a
 * tournament host page editing a scratch copy of the tournament's rules with
 * the same GameSettingsPanel. The helpers close over whichever object they
 * were built for, so the panel does not care which it was handed.
 */
export function createGameSettingsStore(initial?: Partial<MatchRuleSettings> | null) {
  const store = reactive({
    ...defaultSettings,
    ...(initial ?? {}),
  });

  function applyPreset(preset: GameSettingsPreset) {
    const config = presets[preset];
    store.fieldWidth = config.fieldWidth;
    store.fieldHeight = config.fieldHeight;
    store.goalWidth = config.goalWidth;
    store.movementCapEnabled = true;
    store.movementCapMax = config.movementCapMax;
    store.passDistanceEnabled = true;
    store.passDistanceMax = config.passDistanceMax;
    store.saveZoneLength = config.saveZoneLength;
  }

  function resetSettings() {
    // Sound and piece style are device preferences, not game rules -- resetting
    // the rules must not silently change how the board looks.
    const { soundEnabled, pieceStyle, boardTheme } = store;
    Object.assign(store, defaultSettings);
    store.soundEnabled = soundEnabled;
    store.pieceStyle = pieceStyle;
    store.boardTheme = boardTheme;
  }

  return { settings: store, presets, applyPreset, resetSettings };
}

export type GameSettingsStore = ReturnType<typeof createGameSettingsStore>;

// Module-level singleton so settings persist across navigation
const singleton = createGameSettingsStore();
const settings = singleton.settings;

// Sound preference survives reloads (opt-in: off until the user enables it).
const SOUND_STORAGE_KEY = "rasenschach.soundEnabled";
try {
  settings.soundEnabled = localStorage.getItem(SOUND_STORAGE_KEY) === "1";
} catch { /* storage unavailable (e.g. blocked) — keep default */ }
watch(
  () => settings.soundEnabled,
  (on) => {
    try {
      localStorage.setItem(SOUND_STORAGE_KEY, on ? "1" : "0");
    } catch { /* storage unavailable — non-fatal */ }
  },
);

// Piece style is a device preference like sound, so it survives reloads too.
const PIECE_STYLE_STORAGE_KEY = "rasenschach.pieceStyle";
try {
  const stored = localStorage.getItem(PIECE_STYLE_STORAGE_KEY);
  if (stored === "chess" || stored === "shapes") settings.pieceStyle = stored;
} catch { /* storage unavailable — keep default */ }
watch(
  () => settings.pieceStyle,
  (style) => {
    try {
      localStorage.setItem(PIECE_STYLE_STORAGE_KEY, style);
    } catch { /* storage unavailable — non-fatal */ }
  },
);

// Board design, same device-preference treatment as the piece style.
const BOARD_THEME_STORAGE_KEY = "rasenschach.boardTheme";
try {
  const stored = localStorage.getItem(BOARD_THEME_STORAGE_KEY);
  if (stored) settings.boardTheme = stored;
} catch { /* storage unavailable — keep default */ }
watch(
  () => settings.boardTheme,
  (theme) => {
    try {
      localStorage.setItem(BOARD_THEME_STORAGE_KEY, theme);
    } catch { /* storage unavailable — non-fatal */ }
  },
);

export function useGameSettings() {
  return singleton;
}
